import { qModEvent } from "@/utils/eventemitter";
import { store } from "@/stores/global";

/**
 * A versatile fetch composable that handles different HTTP methods and error scenarios
 * Automatically includes webmasterId and sid from global store for consistent API calls
 *
 * @param {string|URL} url - The URL to fetch. If a URL object is provided, it will be converted to string
 * @param {Object} extras - Additional fetch options including method, headers, and body
 * @param {Object} options - Additional options for the composable
 * @param {boolean} options.skipGlobalParams - Skip adding global webmasterId and sid (default: false)
 * @returns {Promise<Object>} - The parsed JSON response or an error object
 */
export const doFetch = async (url, extras = {}, options = { excludeContentType: false }) => {
    // Handle URL objects by converting them to strings
    let urlString = typeof url === "object" && url.toString ? url.toString() : url;

    if (typeof urlString === "object" && urlString.error) return urlString;

    try {
        // Automatically add global parameters unless explicitly skipped
        if (!options.skipGlobalParams) {
            const urlObj = new URL(urlString);

            // Add webmasterId if available and not already present
            if (store.wmid && !urlObj.searchParams.has("webmasterId")) {
                urlObj.searchParams.set("webmasterId", store.wmid);
            }

            // Add sid if available and not already present
            if (store.sid && !urlObj.searchParams.has("sid")) {
                urlObj.searchParams.set("sid", store.sid);
            }

            urlString = urlObj.toString();
        }
        // Configure default options
        const fetchOptions = {
            credentials: "include",
            ...extras,
        };

        // Handle specific needs for POST requests
        if (extras.method === "POST" && extras.body) {
            // Ensure content-type is set correctly for JSON data if not already specified
            if (!extras.headers || !extras.headers["Content-Type"]) {
                fetchOptions.headers = {};

                if (!options.excludeContentType) {
                    fetchOptions.headers["Content-Type"] = "application/json";
                }

                fetchOptions.headers = {
                    ...fetchOptions.headers,
                    ...(extras.headers || {}),
                };
            }
        } // Better log for debugging request bodies
        if (extras.method === "POST" && extras.body) {
            try {
                const bodyObj = JSON.parse(extras.body);
                console.log(
                    `Fetch ${extras.method} request to: ${urlString}`,
                    "with body:",
                    bodyObj
                );
            } catch (e) {
                console.log(
                    `Fetch ${extras.method} request to: ${urlString}`,
                    "with body:",
                    extras.body
                );
            }
        } else {
            console.log(`Fetch ${extras.method || "GET"} request to: ${urlString}`);
        }

        const response = await fetch(urlString, fetchOptions);

        if (!response) {
            return { error: "No response" };
        }

        // Check if the response status indicates an error
        if (!response.ok) {
            // This checks for status codes outside the 2xx range
            let errorBody;
            try {
                errorBody = await response.json();
            } catch (e) {
                // Handle case where response isn't valid JSON
                return {
                    error: response.statusText || "Request failed",
                    status: response.status,
                };
            }

            // Handle specific status codes if needed
            if (response.status === 401 && errorBody.error === "Sid or cookie sid is required") {
                qModEvent({ type: "AUTH", event: "INVALID_SID" });
                return { error: "Please log in" };
            }

            // Return or throw an error message based on the response
            return {
                error: errorBody.error || response.statusText,
                status: response.status,
                details: errorBody,
            };
        }

        return await response.json();
    } catch (e) {
        console.error("Fetch error:", e);
        return { error: e.message || String(e), details: e };
    }
};
